import React from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/app/Header";
import { Button } from "../components/ui/button";
import { ArrowRight, Leaf, MessageCircleHeart, ListChecks, NotebookPen } from "lucide-react";

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    { icon: MessageCircleHeart, title: "Guided conversation", text: "A thoughtful AI companion trained for your exact moment — not generic pep talks." },
    { icon: ListChecks, title: "A plan you can follow", text: "Every situation comes with a small, specific checklist. Tick what you've done." },
    { icon: NotebookPen, title: "A quiet place to write", text: "Journal what's heavy. Track how you're feeling without performing for anyone." },
    { icon: Leaf, title: "Resources that help", text: "Curated helplines, counselors, and guides — no sales pitches, just support." },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg)" }}>
      <Header />

      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              'url("https://images.unsplash.com/photo-1737040455054-f83c122176ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHdhdGVyY29sb3IlMjBzYW5kfGVufDB8fHx8MTc3Njg5Mzk1NXww&ixlib=rb-4.1.0&q=85")',
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(0px)",
            maskImage: "linear-gradient(to bottom, black 0%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-28 md:pt-32 md:pb-40">
          <div className="max-w-3xl fade-in">
            <p
              className="text-xs uppercase tracking-[0.25em] mb-6"
              style={{ color: "var(--muted)" }}
              data-testid="landing-eyebrow"
            >
              A companion for hard seasons
            </p>
            <h1
              className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-8"
              style={{ color: "var(--ink)" }}
              data-testid="landing-title"
            >
              When life gets heavy,
              <br />
              <em className="not-italic" style={{ color: "var(--terracotta)" }}>
                take one step at a time.
              </em>
            </h1>
            <p
              className="text-lg md:text-xl leading-relaxed max-w-xl mb-10"
              style={{ color: "var(--ink-soft)" }}
              data-testid="landing-sub"
            >
              A sick parent. A newborn. A career at a crossroads. Money pressure that won't
              quiet down. Anchor is a calm place to think it through — with steady guidance,
              small plans, and a journal for the in-between.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate("/auth?mode=signup")}
                className="rounded-full h-12 px-7 text-white text-base"
                style={{ backgroundColor: "var(--terracotta)" }}
                data-testid="cta-begin-btn"
              >
                Begin with a free account
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/auth?mode=login")}
                className="rounded-full h-12 px-6 text-base"
                data-testid="cta-signin-btn"
              >
                I already have an account
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 stagger">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-3xl p-7 border bg-white"
              style={{ borderColor: "var(--border)" }}
              data-testid={`feature-${f.title.toLowerCase().split(" ")[0]}`}
            >
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center mb-5"
                style={{ backgroundColor: "var(--surface-2)" }}
              >
                <f.icon className="w-5 h-5" style={{ color: "var(--terracotta)" }} />
              </div>
              <h3 className="font-serif text-2xl mb-2" style={{ color: "var(--ink)" }}>
                {f.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                {f.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      <footer
        className="border-t py-8 text-center text-xs"
        style={{ borderColor: "var(--border)", color: "var(--muted)" }}
      >
        Anchor · Gentle guidance for hard days · Not a substitute for medical or financial advice.
      </footer>
    </div>
  );
};

export default Landing;
